import {includes} from '@shared/helpers/collection'
import {formatDateFns, getFormattedTime, TimeFormat} from '@shared/helpers/date'
import {getDeliveryTodayMessage, numberFormat} from '@shared/helpers/format'
import {eq, isNil, toNumber} from '@shared/helpers/lang'

import {
    Card,
    DELIVERY_MEMO,
    DeliveryAttributeCode,
    DeliveryAttributeCodeType,
    DeliveryMemoType,
    PayMethod,
    TodayDispatchNotAppliedReason,
} from '@web/core/constants'
import {getArrivalGuaranteeText, isArrivalGuarantee} from '@web/helpers/policies'

import {
    Branch,
    DeliveryPrediction,
    HopeDelivery,
    PreOrder,
    ResultProduct,
    ResultState,
    TodayDispatch,
} from '../interfaces'

const getInitialPayAdmissionState = () => {
    return {
        payMethodType: PayMethod.NONE,
        admissionDateTime: '',
        cardSettleInfo: {
            cardCompanyName: '',
            maskedCardNumber: '',
            cardType: Card.CREDIT,
        },
        freeInterestInstallmentInfo: {
            freeInterestInstallmentApply: false,
            installmentMonth: 0,
            partFreeInterestBurdenMonths: '',
        },
        mobileSettleInfo: {
            cellPhoneNumber: '',
            mobileOperator: '',
        },
        pointSettleInfo: {
            extraChargedPointAmount: 0,
        },
        realTimeAccountTransferSettle: {
            realTimeAccountTransferAccountHolderName: '',
            realTimeAccountTransferBankName: '',
        },
        virtualAccountSettleInfo: {
            payExpirationDateTime: '',
            virtualAccountHolderName: '',
            virtualAccountNumber: '',
            virtualAccountBankName: '',
        },
    }
}

export const getInitialState = (): ResultState => ({
    totalDeliveryFeeAmount: 0,
    totalDiscountAmount: 0,
    totalOrderAmount: 0,
    totalPurchaserCommissionAmount: 0,
    firstSubscriptionDiscountAmount: 0,
    order: {
        deliveryMemoParticularInput: false,
        orderNo: '',
        orderSheetId: '',
        orderOptionInfoParticularInput: false,
        todayDispatchIncluded: false,
        dawnArrivalIncluded: false,
        preOrderIncluded: false,
        deliveryChangeable: false,
    },
    pay: {
        chargePointPayAmount: 0,
        generalPayAmount: 0,
        payAdmission: getInitialPayAdmissionState(),
        payLaterAmount: 0,
        payMethodType: PayMethod.NONE,
        rewardPointPayAmount: 0,
        totalPayAmount: 0,
        rechargePointAccountName: '',
        rechargePointBankName: '',
        rechargePointPayAmount: 0,
        rechargedByNaverBankbook: false,
        isCardPromotionUsed: false,
        payLaterPayDueDate: '',
        benefitCardRewardTid: null,
    },
    deposit: null,
    products: [],
    purchaser: {
        alarmType: null,
        chargePointUserStatusType: null,
        name: null,
        naverId: null,
        naverIdNo: null,
    },
    deliveryAddressBook: null,
    delivery: {
        deliveryGroupsByKey: {},
        totalDeliveryFee: 0,
    },
    deliveryNothingOrder: false,
    toastPopup: {
        expectTotalRewardPointOfTodayOrders: 0,
        joinMembershipBannerExposure: false,
        rechargePointPaymentBannerExposure: false,
    },
    isMembershipPointLoading: false,
    pendingPayment: false,
    merchantsByMerchantNo: {},
    cartCount: 0,
    familyPayOrder: null,
    exposeFamilyPayBanner: {
        member: false,
        organizer: false,
    },
    gifting: {
        receiverCount: 0,
        productOrderGiftingInfos: [],
    },
    orderKey: '',
})

export const getDeliveryMemoType = (value: string, isTemplateMemo: boolean): DeliveryMemoType => {
    if (value) {
        return isTemplateMemo ? DELIVERY_MEMO.COMMON : DELIVERY_MEMO.WRITTEN
    }
    return DELIVERY_MEMO.NOTHING
}

const getTodayDispatchText = (
    todayDispatch: TodayDispatch,
    dispatchDueDateTime: string,
    isTodayDispatchUnExposed: boolean,
) => {
    const {todayDispatchBasisTime, todayDispatchNotAppliedReason} = todayDispatch
    const reasonForNotApplyingIncludes = includes(todayDispatchNotAppliedReason)
    if (
        !todayDispatchBasisTime ||
        isTodayDispatchUnExposed ||
        reasonForNotApplyingIncludes([
            TodayDispatchNotAppliedReason.NOT_ENOUGH_STOCK,
            TodayDispatchNotAppliedReason.OUT_OF_STOCK,
        ])
    ) {
        return ''
    }

    if (
        reasonForNotApplyingIncludes([
            // 휴일케이스
            TodayDispatchNotAppliedReason.HOLIDAY_OF_THE_WEEK,
            TodayDispatchNotAppliedReason.NO_SALES_DAY,
            TodayDispatchNotAppliedReason.SELLER_HOLIDAY,
            // 마감시간 초과 케이스
            TodayDispatchNotAppliedReason.OVERDUE_TIME,
        ])
    ) {
        const dateText = formatDateFns(dispatchDueDateTime, TimeFormat['M. d.(EEE)'], {
            inputFormat: `yyyy-MM-dd'T'HH:mm:ss'Z'`,
        })
        return dateText ? `${dateText} 발송예정` : ''
    }

    const basisTime = `${todayDispatchBasisTime.slice(0, 2)}:${todayDispatchBasisTime.slice(2, 4)}`
    return `${basisTime} 까지 결제 시 오늘 발송`
}

const getDeliveryPredictionText = (deliveryPrediction: DeliveryPrediction) => {
    const {predictionDate, prediction, visible} = deliveryPrediction
    // TODO: 노출여부를 보고 판단하는지 여부
    if (!predictionDate || !visible) {
        return ''
    }

    return `${formatDateFns(predictionDate, 'M. d.(EEE)')} 도착확률 ${prediction}%`
}

const getHopeDeliveryText = (hopeDelivery: HopeDelivery) => {
    const {date, region, additionalFee, time} = hopeDelivery
    if (!date && !region) {
        return ''
    }

    if (!date && region) {
        return '배송일은 판매자 상담 후 확인 가능'
    }

    const regionText = region ? ` ${region}` : ''
    const additionalFeeText = additionalFee > 0 ? ` ${numberFormat(additionalFee)}원` : ' 무료배송'
    const hopeDeliveryTimeText = time ?? ''

    return `${formatDateFns(
        date ?? 0,
        'M. d.(EEE)',
    )} 배송희망 ${hopeDeliveryTimeText} /${regionText}${additionalFeeText}`
}

const getPreOrderText = (preOrder: PreOrder) => {
    const {deliveryStartDateTime, deliveryEndDateTime} = preOrder
    const start = deliveryStartDateTime ? formatDateFns(deliveryStartDateTime, 'M. d.(EEE)') : ''
    const end = deliveryEndDateTime ? formatDateFns(deliveryEndDateTime, 'M. d.(EEE)') : ''
    const period = `${start ? `${start}~` : ''}${end}` || ''
    if (!period) {
        return ''
    }
    return `${period} 발송예정`
}

const getBranchOrderText = (
    branch: Branch,
    deliveryAttributeType: Maybe<DeliveryAttributeCodeType>,
    orderDateTime: string,
) => {
    const {
        delivery: {date, startTime, endTime, preparationMinutes, durationMinutes, slotAvailable},
    } = branch
    const isDawnArrival = eq(deliveryAttributeType, DeliveryAttributeCode.DAWN_ARRIVAL)
    const isTodayArrival = eq(deliveryAttributeType, DeliveryAttributeCode.TODAY_ARRIVAL) && slotAvailable
    const isImmediateArrival = eq(deliveryAttributeType, DeliveryAttributeCode.TODAY_ARRIVAL) && !slotAvailable
    const isQuick = eq(deliveryAttributeType, DeliveryAttributeCode.QUICK)
    const isPickup = eq(deliveryAttributeType, DeliveryAttributeCode.PICKUP)
    const isNormal = eq(deliveryAttributeType, DeliveryAttributeCode.NORMAL)

    const formattedDate = formatDateFns(date, 'M. d.(EEE)')
    if (isNil(formattedDate)) {
        return ''
    }

    if (isDawnArrival && endTime) {
        return `${formattedDate} ${endTime} 까지 배송`
    }

    if (isNormal && endTime) {
        return `${formattedDate} 까지 배송`
    }

    if (isTodayArrival && startTime && endTime) {
        return `${formattedDate} ${startTime}~${endTime}에 배송예정`
    }

    const immediateArrivalMessage = getDeliveryTodayMessage(preparationMinutes ?? 0)
    if (isImmediateArrival && immediateArrivalMessage) {
        return `${formattedDate} ${immediateArrivalMessage} 내외 즉시배송`
    }

    if (isQuick) {
        return `${formattedDate} ${getDeliveryTodayMessage(preparationMinutes + durationMinutes)} 내외 즉시배달`
    }

    if (isPickup) {
        return `${formattedDate} ${getFormattedTime(orderDateTime, {minutesToAdd: preparationMinutes})}부터 픽업가능`
    }

    return ''
}

/**
 * - 배송 속성 우선순위: 도착보장 > 오늘출발 > 배송 시뮬레이터 > 배송 희망일 > 예약구매 > 주문확인 후 제작
 * - 배송일 문구: 각 속성별 배송일 문구
 */
export const getDeliveryInfo = ({
    product,
    isTodayDispatchUnExposed,
}: {
    product: ResultProduct
    isTodayDispatchUnExposed: boolean
}): {
    color?: string
    priority: number
    startDate: Date | null
    endDate: Date | null
    message: string
} => {
    const {
        deliveryAttributeType,
        todayDispatch,
        dispatchDueDateTime,
        deliveryPrediction,
        hopeDelivery,
        preOrder,
        arrivalGuarantee,
        branch,
        installationProduct,
        orderDateTime,
        deliveryTagType,
    } = product

    if (isArrivalGuarantee(deliveryAttributeType)) {
        const message = getArrivalGuaranteeText({
            date: arrivalGuarantee?.arrivalGuaranteeDateTime,
            postfix: installationProduct ? '설치' : '도착보장',
            deliveryTagType,
        })
        const date = message ? new Date(arrivalGuarantee?.arrivalGuaranteeDateTime || '') : null
        return {
            priority: 1,
            message,
            startDate: date,
            endDate: date,
        }
    } else if (todayDispatch?.todayDispatch) {
        const message = getTodayDispatchText(todayDispatch, dispatchDueDateTime, isTodayDispatchUnExposed)
        const basisTime = todayDispatch.todayDispatchBasisTime || '00:00'
        const date = message ? new Date() : null
        date && date.setHours(toNumber(basisTime.slice(0, 2)), toNumber(basisTime.slice(2, 4)))
        return {
            priority: 2,
            message,
            startDate: date,
            endDate: date,
        }
    } else if (deliveryPrediction) {
        const message = getDeliveryPredictionText(deliveryPrediction)
        const date = message ? new Date(deliveryPrediction.predictionDate || '') : null
        return {
            priority: 3,
            message,
            startDate: date,
            endDate: date,
        }
    } else if (hopeDelivery) {
        const message = getHopeDeliveryText(hopeDelivery)
        const date = message ? new Date(hopeDelivery.date) : null
        return {
            priority: 4,
            message,
            startDate: date,
            endDate: date,
        }
    } else if (preOrder) {
        const message = getPreOrderText(preOrder)
        const startDate = message ? new Date(preOrder.deliveryStartDateTime || '') : null
        const endDate = message ? new Date(preOrder.deliveryEndDateTime || '') : null
        return {
            priority: 5,
            message,
            startDate,
            endDate,
        }
    } else if (branch) {
        const message = getBranchOrderText(branch, deliveryAttributeType, orderDateTime)
        return {
            priority: 6,
            message,
            startDate: null,
            endDate: null,
        }
    }
    return {
        priority: 7,
        message: '',
        startDate: null,
        endDate: null,
    }
}
